import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LogIn, ShieldCheck, AlertCircle, Loader2 } from "lucide-react";
import { useAdminAuth } from "@/admin/AdminAuthContext";

/**
 * Admin sign-in screen.
 *
 * Primary flow: email + password (for admins seeded with a bcrypt password).
 * Secondary flow: Continue with Google (kept for any Gmail-based admin accounts
 * already using Emergent Google OAuth).
 *
 * REMINDER: DO NOT HARDCODE THE BACKEND URL, OR ADD ANY FALLBACKS OR REDIRECT
 * URLS FOR THE GOOGLE FLOW. The redirect URL is derived at click time from
 * window.location.origin so it matches whichever preview / production host
 * the admin is currently on.
 */

// API calls use relative `/api/...` paths. See ContentProvider.jsx
// for the rationale (avoids build-time hostname binding bugs).

export default function AdminLogin() {
  const { status, admin, refresh } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // If the admin is already authenticated (e.g. revisiting this page with a
  // live session cookie), bounce straight to the dashboard or the page they
  // originally wanted to reach.
  useEffect(() => {
    if (status === "authenticated" && admin) {
      const dest = location.state?.from || "/admin";
      navigate(dest, { replace: true });
    }
  }, [status, admin, navigate, location.state]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showGoogle, setShowGoogle] = useState(false);

  // Read ?error=... that the direct Google OAuth callback may attach when
  // it bounces the admin back here (e.g. not_allowed, verify, missing_code).
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const code = params.get("error");
    if (!code) return;
    let msg;
    if (code === "not_allowed") {
      msg = "This Google account is not authorised for admin access.";
    } else if (code === "google_unavailable") {
      msg = "Google sign-in is temporarily unavailable.";
    } else if (code === "access_denied") {
      msg = "Sign-in was cancelled before it completed.";
    } else if (code === "no_email") {
      msg = "Google didn\u2019t return a valid email address.";
    } else if (code === "invalid_state") {
      msg = "The sign-in session expired. Please try again.";
    } else {
      msg = "Sign-in with Google failed. Please try again.";
    }
    setErrorMsg(msg);
    setShowGoogle(true);
    // Strip the error param from the URL so a refresh doesn't re-show it.
    if (typeof window !== "undefined" && window.history?.replaceState) {
      const clean = window.location.pathname;
      window.history.replaceState(null, "", clean);
    }
  }, [location.search]);

  const startGoogleLogin = () => {
    // SAS-owned Google OAuth: hits our backend, which generates a CSRF
    // state and 302-redirects to Google. After consent, Google posts the
    // code back to /api/admin/auth/google/callback which sets the
    // admin_session cookie and 303s to /admin.
    window.location.href = `/api/admin/auth/google/start`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!email.trim() || !password) {
      setErrorMsg("Please enter your email and password.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/admin/auth/password-login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      if (!res.ok) {
        if (res.status === 401) {
          setErrorMsg("Invalid email or password.");
        } else if (res.status === 422) {
          setErrorMsg("Please check your email format.");
        } else {
          setErrorMsg("Sign-in failed. Please try again.");
        }
        return;
      }
      // Re-hit /me so the context flips to authenticated, then redirect.
      await refresh();
      const dest = location.state?.from || "/admin";
      navigate(dest, { replace: true });
    } catch {
      setErrorMsg("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[color:var(--sas-accent-soft)]/30 px-4"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-[color:var(--sas-line)] shadow-sm p-8">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center mb-6"
            style={{ background: "var(--sas-accent-soft)", color: "var(--sas-accent)" }}
            aria-hidden
          >
            <ShieldCheck size={20} />
          </div>

          <div className="sas-eyebrow mb-2">SAS · Admin</div>
          <h1 className="sas-h2">Admin sign-in</h1>
          <p className="sas-body mt-3 text-[14.5px]">
            Access to the admin area is restricted to authorised maintainers of
            the Syrian AI Society website.
          </p>

          <form onSubmit={onSubmit} className="mt-7 flex flex-col gap-4" noValidate>
            {errorMsg && (
              <div
                className="flex items-start gap-2 rounded-lg border border-[color:#fecaca] bg-[#fef2f2] px-3 py-2.5 text-[13px] text-[#991b1b]"
                role="alert"
                data-testid="admin-login-error"
              >
                <AlertCircle size={15} className="mt-0.5 shrink-0" aria-hidden />
                <span>{errorMsg}</span>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label htmlFor="admin-login-email" className="text-[12.5px] font-semibold text-[color:var(--sas-ink)]">
                Email
              </label>
              <input
                id="admin-login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="sas-input"
                placeholder="admin@example.org"
                data-testid="admin-login-email"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="admin-login-password" className="text-[12.5px] font-semibold text-[color:var(--sas-ink)]">
                Password
              </label>
              <input
                id="admin-login-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="sas-input"
                placeholder="Enter your password"
                data-testid="admin-login-password"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="sas-btn sas-btn-primary w-full justify-center mt-2"
              data-testid="admin-login-submit"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" aria-hidden />
                  Signing in…
                </>
              ) : (
                <>
                  <LogIn size={16} aria-hidden />
                  Sign in
                </>
              )}
            </button>
          </form>

          <div className="mt-6 border-t border-[color:var(--sas-line)] pt-5">
            {showGoogle ? (
              <button
                type="button"
                onClick={startGoogleLogin}
                className="sas-btn sas-btn-ghost w-full justify-center"
                data-testid="admin-login-google"
              >
                Continue with Google
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowGoogle(true)}
                className="text-xs text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)] underline-offset-4 hover:underline mx-auto block"
                data-testid="admin-login-show-google"
              >
                Use Google sign-in instead
              </button>
            )}
          </div>

          <p className="text-xs text-[color:var(--sas-ink-2)] mt-6 leading-relaxed">
            Sessions last 7 days. Every request is verified server-side, and
            only allowlisted accounts are permitted.
          </p>
        </div>

        <div className="text-center mt-6">
          <a
            href="/"
            className="text-xs text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)]"
            data-testid="admin-login-back-to-site"
          >
            ← Back to the public site
          </a>
        </div>
      </div>
    </div>
  );
}
